import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faSort, faSortAsc, faSortDesc} from '@fortawesome/free-solid-svg-icons';
import {action, observable, reaction} from 'mobx';
import {observer, PropTypes as MobxPropTypes} from 'mobx-react';
import moment from 'moment';
import React, {Component} from 'react';

import {STATE_FULFILLED} from '../../../constants/asyncConstants';
import {MOMENT_LONG_DATE_FORMAT} from '../../../constants/uiConstants';
import {DEFAULT_CONTENT_REQUESTS_QUERY} from '../../../stores/api/contentRequest/apiAdminContentRequestGetAllStore';
import Link from '../../common/link/Link';
import SmartTableSearch from '../../common/smartTable/SmartTableSearch';
import {parseSearchParams} from '../../../utils/routeHelper';
import inject from '../../hoc/injectHoc';
import {adminContentRequestRoute, adminContentRequestsListRoute, dashboardRoute} from '../../routePaths';

import './adminContentRequestsListPage.scss';

/**
 * The per page options.
 *
 * @type {number[]}
 */
// eslint-disable-next-line no-magic-numbers
const PER_PAGE_OPTIONS = [20, 50, 100];

/**
 * How many page number links to show on either side of the current page.
 *
 * @type {number}
 */
const PAGE_WINDOW = 2;

/**
 * The table columns. `sortKey` marks a column the API can sort by.
 *
 * @type {Array<{key: string, label: string, sortKey?: string}>}
 */
const COLUMNS = [
  {key: 'name', label: 'REQUEST ID', sortKey: 'id'},
  {key: 'user', label: 'USER'},
  {key: 'purpose', label: 'PURPOSE', sortKey: 'purpose'},
  {key: 'emails', label: 'EMAILS'},
  {key: 'dateSubmitted', label: 'DATE SUBMITTED', sortKey: 'createdAt'},
];

/**
 * The sort keys the API accepts.
 *
 * @type {string[]}
 */
const SORT_KEYS = COLUMNS.filter((column) => column.sortKey).map((column) => column.sortKey);

/**
 * The largest perPage accepted from the URL.
 *
 * @type {number}
 */
const MAX_PER_PAGE = 100;

/**
 * Parses a positive integer, falling back to the default when invalid.
 *
 * @param {string} value
 * @param {number} fallback
 * @returns {number}
 */
function parsePositiveInt(value, fallback) {
  const parsed = parseInt(value, 10);
  return (Number.isFinite(parsed) && parsed > 0) ? parsed : fallback;
}

/**
 * Parses the URL search string into a safe query, falling back to defaults for missing/invalid values.
 *
 * @param {string} search
 * @returns {{page: number, perPage: number, search: string, sort: string, direction: string}}
 */
function parseQueryFromSearch(search) {
  const params = parseSearchParams(search);
  const defaults = DEFAULT_CONTENT_REQUESTS_QUERY;

  return {
    page: parsePositiveInt(params.page, defaults.page),
    perPage: Math.min(parsePositiveInt(params.perPage, defaults.perPage), MAX_PER_PAGE),
    search: params.search ? String(params.search) : defaults.search,
    sort: SORT_KEYS.includes(params.sort) ? params.sort : defaults.sort,
    direction: (params.direction === 'asc' || params.direction === 'desc') ? params.direction : defaults.direction,
  };
}

/**
 * Builds the URL search string from a query, leaving out values that match the defaults.
 *
 * @param {{}} query
 * @returns {string}
 */
function buildSearchFromQuery(query) {
  const params = new URLSearchParams();

  Object.keys(DEFAULT_CONTENT_REQUESTS_QUERY).forEach((key) => {
    const value = query[key];
    if (value !== '' && value !== null && typeof value !== 'undefined' && value !== DEFAULT_CONTENT_REQUESTS_QUERY[key]) {
      params.set(key, String(value));
    }
  });

  const search = params.toString();
  return search ? `?${search}` : '';
}

/**
 * The AdminContentRequestListPage component.
 */
export class AdminContentRequestsListPage extends Component {
  /**
   * The current query sent to the API. Always derived from the URL search params.
   *
   * @type {{page: number, perPage: number, search: string, sort: string, direction: string}}
   */
  @observable query = {...DEFAULT_CONTENT_REQUESTS_QUERY};

  /**
   * Disposes the URL reaction.
   *
   * @type {?function}
   */
  disposeUrlReaction = null;

  /**
   * Triggered when the component first mounts to the page.
   * The URL is the source of truth: any change to it (our own controls, back/forward, refresh) refetches.
   */
  componentDidMount() {
    const {routerStore} = this.props;

    this.disposeUrlReaction = reaction(
      () => (routerStore.location ? routerStore.location.search : ''),
      (search) => {
        const pathname = routerStore.location ? routerStore.location.pathname : '';
        if (pathname.replace(/\/$/, '') !== adminContentRequestsListRoute) {
          return;
        }

        this.setQuery(parseQueryFromSearch(search));
        this.fetchRequests();
      },
      {fireImmediately: true}
    );
  }

  /**
   * Triggered when the component is about to unmount.
   */
  componentWillUnmount() {
    if (this.disposeUrlReaction) {
      this.disposeUrlReaction();
      this.disposeUrlReaction = null;
    }
  }

  /**
   * Sets the query.
   *
   * @param {{}} newQuery
   */
  @action setQuery = (newQuery) => {
    this.query = newQuery;
  };

  /**
   * Writes the changed query to the URL, which triggers the fetch via the URL reaction.
   *
   * @param {{}} changes
   */
  updateQuery = (changes) => {
    const {routerStore} = this.props;

    routerStore.replace({
      pathname: adminContentRequestsListRoute,
      search: buildSearchFromQuery({...this.query, ...changes}),
    });
  };

  /**
   * Fetches the content requests for the current query.
   */
  fetchRequests = () => {
    const {apiAdminContentRequestGetAllStore} = this.props;

    apiAdminContentRequestGetAllStore.refresh({...this.query});
  };

  /**
   * Triggered when a search is submitted.
   *
   * @param {string} search
   */
  onSearch = (search) => {
    this.updateQuery({search: search || '', page: 1});
  };

  /**
   * Triggered when the search is cleared.
   */
  onSearchClear = () => {
    this.updateQuery({search: '', page: 1});
  };

  /**
   * Toggles sorting for a column (desc -> asc -> desc).
   *
   * @param {string} sortKey
   * @returns {function}
   */
  onSortClicked = (sortKey) => {
    return (clickEvent) => {
      clickEvent.preventDefault();

      const {sort, direction} = this.query;
      const newDirection = (sort === sortKey && direction === 'desc') ? 'asc' : 'desc';

      this.updateQuery({sort: sortKey, direction: newDirection, page: 1});
    };
  };

  /**
   * Navigates to the given page.
   *
   * @param {number} page
   * @returns {function}
   */
  onPageClicked = (page) => {
    return (clickEvent) => {
      clickEvent.preventDefault();

      if (page === this.query.page) {
        return;
      }

      this.updateQuery({page});
    };
  };

  /**
   * Triggered when the per page select changes.
   *
   * @param {{target: {value: string}}} changeEvent
   */
  onPerPageChange = (changeEvent) => {
    this.updateQuery({perPage: Number(changeEvent.target.value), page: 1});
  };

  /**
   * Parses the content requests into the table rows.
   *
   * @param {Array<{}>} requests
   * @returns {Array<{}>}
   */
  parseRequestsToRow = (requests) => {
    return requests.map((request) => {
      const user = request.user
        ? `${request.user.fullName} (${request.user.email})`
        : '';

      return {
        id: request.id,
        name: (
          <Link
            className="btn btn-sm btn-primary"
            to={adminContentRequestRoute}
            params={{requestId: request.id}}
          >{request.id}</Link>
        ),
        user,
        purpose: request.purpose,
        emails: (request.emails) ? request.emails.join(', ') : '',
        dateSubmitted: moment(request.createdAt).format(MOMENT_LONG_DATE_FORMAT),
      };
    });
  };

  /**
   * Renders the table header.
   *
   * @returns {{}}
   */
  renderTableHead() {
    const {sort, direction} = this.query;

    return (
      <thead className="table-header">
        <tr>
          {COLUMNS.map((column) => {
            if (!column.sortKey) {
              return <th scope="col" key={column.key}>{column.label}</th>;
            }

            const isSorted = (sort === column.sortKey);
            let icon = faSort;
            if (isSorted) {
              icon = (direction === 'asc') ? faSortAsc : faSortDesc;
            }

            return (
              <th
                scope="col"
                key={column.key}
                className="sortable"
                onClick={this.onSortClicked(column.sortKey)}
              >
                <span className="column-label">{column.label}</span>
                <span className={isSorted ? 'pull-right ml-1' : 'show-on-hover pull-right ml-1'}>
                  <FontAwesomeIcon icon={icon} />
                </span>
              </th>
            );
          })}
        </tr>
      </thead>
    );
  }

  /**
   * Renders a full-width message row.
   *
   * @param {{}} content
   * @returns {{}}
   */
  renderMessageRow(content) {
    return (
      <tr>
        <td colSpan={COLUMNS.length}>{content}</td>
      </tr>
    );
  }

  /**
   * Renders the table body.
   *
   * @returns {{}}
   */
  renderTableBody() {
    const {apiAdminContentRequestGetAllStore} = this.props;

    return (
      <tbody>
        {apiAdminContentRequestGetAllStore.case({
          pre: () => this.renderMessageRow('Loading...'),
          pending: () => this.renderMessageRow('Loading...'),
          rejected: () => this.renderMessageRow(
            <span className="text-warning">The list of content requests could not be loaded.</span>
          ),
          fulfilled: (requests) => {
            if (!requests || !requests.length) {
              return this.renderMessageRow('No content requests found.');
            }

            return this.parseRequestsToRow(requests).map((row) => (
              <tr key={row.id}>
                {COLUMNS.map((column) => (
                  <td key={column.key}>{row[column.key]}</td>
                ))}
              </tr>
            ));
          },
        })}
      </tbody>
    );
  }

  /**
   * Builds the list of page numbers to show, with null marking a gap.
   *
   * @param {number} currentPage
   * @param {number} lastPage
   * @returns {Array<?number>}
   */
  getPageNumbers(currentPage, lastPage) {
    const start = Math.max(1, currentPage - PAGE_WINDOW);
    const end = Math.min(lastPage, currentPage + PAGE_WINDOW);

    const pages = [];
    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push(null);
      }
    }
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    if (end < lastPage) {
      if (end < lastPage - 1) {
        pages.push(null);
      }
      pages.push(lastPage);
    }

    return pages;
  }

  /**
   * Renders the pagination controls.
   *
   * @returns {{}}
   */
  renderPagination() {
    const {apiAdminContentRequestGetAllStore} = this.props;
    const meta = apiAdminContentRequestGetAllStore.meta;
    const isLoading = (apiAdminContentRequestGetAllStore.state !== STATE_FULFILLED);

    const currentPage = (meta && meta.currentPage) || this.query.page;
    const lastPage = (meta && meta.lastPage) || 1;
    const total = (meta && meta.total) || 0;
    const perPage = this.query.perPage;

    const firstItem = total ? ((currentPage - 1) * perPage) + 1 : 0;
    const lastItem = Math.min(currentPage * perPage, total);

    const pageLink = (page, label, disabled, key) => (
      <li key={key || page} className={`page-item${disabled ? ' disabled' : ''}${page === currentPage && !label ? ' active' : ''}`}>
        <a className="page-link" href="#" onClick={disabled ? (e) => e.preventDefault() : this.onPageClicked(page)}>
          {label || page}
        </a>
      </li>
    );

    return (
      <div className="content-requests-pagination d-flex flex-wrap align-items-center justify-content-between">
        <div className="pagination-summary">
          {meta ? `Showing ${firstItem}–${lastItem} of ${total}` : ' '}
          <label className="per-page ml-3 mb-0">
            Per page
            <select
              className="form-control form-control-sm d-inline-block ml-2"
              value={perPage}
              onChange={this.onPerPageChange}
            >
              {PER_PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </label>
        </div>

        {(lastPage > 1) && (
          <nav aria-label="Content requests pages">
            <ul className="pagination mb-0">
              {pageLink(currentPage - 1, '‹ Prev', isLoading || currentPage <= 1, 'prev')}
              {this.getPageNumbers(currentPage, lastPage).map((page, index) => {
                if (page === null) {
                  return (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`gap-${index}`} className="page-item disabled">
                      <span className="page-link">…</span>
                    </li>
                  );
                }
                return pageLink(page, null, isLoading);
              })}
              {pageLink(currentPage + 1, 'Next ›', isLoading || currentPage >= lastPage, 'next')}
            </ul>
          </nav>
        )}
      </div>
    );
  }

  /**
   * Renders the component.
   *
   * @returns {{}}
   */
  render() {
    return (
      <div id="admin-content-request-list-page" className="system-page full-height">
        <div className="container">
          <Link to={dashboardRoute}>
            <h4 className="h6">← Back to Dashboard</h4>
          </Link>

          <div className="smart-table">
            <header className="smart-table-header">
              <div className="row">
                <div className="col-sm-6 col-sm-offset-6">
                  <SmartTableSearch
                    value={this.query.search}
                    onSearch={this.onSearch}
                    onClear={this.onSearchClear}
                  />
                </div>
              </div>
            </header>

            <table className="table table-sortable table-dark table-striped table-hover">
              {this.renderTableHead()}
              {this.renderTableBody()}
            </table>

            {this.renderPagination()}
          </div>
        </div>
      </div>
    );
  }
}

AdminContentRequestsListPage.propTypes = {
  apiAdminContentRequestGetAllStore: MobxPropTypes.observableObject,
  routerStore: MobxPropTypes.observableObject,
};

AdminContentRequestsListPage.wrappedComponent = {};
AdminContentRequestsListPage.wrappedComponent.propTypes = {
  apiAdminContentRequestGetAllStore: MobxPropTypes.observableObject.isRequired,
  routerStore: MobxPropTypes.observableObject.isRequired,
};

export default inject(AdminContentRequestsListPage)(
  observer(AdminContentRequestsListPage)
);
